import { BriefcaseBusiness, MapPin } from 'lucide-react'
import { Link } from 'react-router-dom'
import { getTalentTypeIcon, getTalentTypeLabel } from '../../utils/talentTypeUtils'
import { getProfessionName } from '../../utils/professionUtils'
import Avatar from '../UI/Avatar'
import FavoriteStar from '../Favorites/FavoriteStar'

const MetadataItem = ({ label, children }) => (
  <div className="min-w-0">
    <dt className="text-xs font-medium uppercase tracking-wide text-light-text-tertiary dark:text-dark-text-tertiary">{label}</dt>
    <dd className="mt-1 break-words text-sm text-light-text-primary dark:text-dark-text-primary">{children || 'Not specified'}</dd>
  </div>
)

const TalentCard = ({ talent, fromHref, manager, actions }) => {
  const TypeIcon = getTalentTypeIcon(talent.talent_type)
  const primaryProfession = talent.professions?.find((profession) => profession.is_primary) || talent.professions?.[0]
  const location = [talent.city?.name, talent.country?.name].filter(Boolean).join(', ')
  const displayName = [talent.last_name, talent.first_name].filter(Boolean).join(' ') || `Talent #${talent.id}`
  const firstContact = talent.authUser?.email || talent.contacts?.[0]?.value
  const contactValue = typeof firstContact === 'object' ? firstContact?.value : firstContact

  return (
    <article className="flex h-full min-w-0 flex-col rounded-xl border border-light-border-primary bg-light-bg-primary p-4 shadow-sm dark:border-dark-border-primary dark:bg-dark-bg-secondary">
      <div className="flex min-w-0 items-start gap-3">
        <div className="shrink-0 rounded-full bg-gradient-to-br from-primary-400 to-purple-500 p-0.5 shadow-sm">
          <Avatar
            avatar={talent.authUser?.avatar}
            name={talent.authUser?.name || displayName}
            email={talent.authUser?.email}
            sizeClasses="h-12 w-12"
            iconSizeClasses="h-5 w-5"
            icon={TypeIcon}
            showInitialOnMissing
          />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-start justify-between gap-2">
            <div className="min-w-0">
              <Link
                to={`/talents/${talent.id}`}
                state={{ from: fromHref }}
                className="break-words text-base font-semibold text-primary-700 hover:underline dark:text-primary-300"
              >
                {displayName}
              </Link>
              <div className="mt-0.5 text-xs text-light-text-tertiary dark:text-dark-text-tertiary">
                ID: {talent.id} · {getTalentTypeLabel(talent.talent_type) || 'Talent'}
              </div>
            </div>
            <FavoriteStar talentId={talent.id} />
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {talent.currentStatus && (
              <span
                className="inline-flex rounded-full px-2 py-1 text-xs font-semibold text-white"
                style={{ backgroundColor: talent.currentStatus.color || '#64748b' }}
              >
                {talent.currentStatus.name}
              </span>
            )}
            {talent.currentStage && (
              <span className="inline-flex rounded-full bg-blue-100 px-2 py-1 text-xs font-medium text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                {talent.currentStage.name}
              </span>
            )}
            <span className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${
              talent.is_active === false
                ? 'bg-light-bg-tertiary text-light-text-secondary dark:bg-dark-bg-tertiary dark:text-dark-text-secondary'
                : 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
            }`}>
              {talent.is_active === false ? 'Inactive' : 'Active'}
            </span>
          </div>
        </div>
      </div>

      <dl className="mt-4 grid min-w-0 grid-cols-1 gap-3 border-y border-light-border-primary py-4 dark:border-dark-border-primary sm:grid-cols-2">
        <MetadataItem label="Profession">
          {primaryProfession ? (
            <span className="inline-flex items-start gap-1.5">
              <BriefcaseBusiness className="mt-0.5 h-4 w-4 shrink-0 text-light-text-tertiary dark:text-dark-text-tertiary" />
              {getProfessionName(primaryProfession)}
            </span>
          ) : null}
        </MetadataItem>
        <MetadataItem label="Manager">{manager?.name || manager?.email}</MetadataItem>
        <MetadataItem label="Location">
          {location ? (
            <span className="inline-flex items-start gap-1.5">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-light-text-tertiary dark:text-dark-text-tertiary" />
              {location}
            </span>
          ) : null}
        </MetadataItem>
        <MetadataItem label="Contact">{contactValue}</MetadataItem>
      </dl>

      <div className="mt-auto flex min-w-0 flex-col gap-3 pt-4">
        <Link
          to={`/talents/${talent.id}`}
          state={{ from: fromHref }}
          className="inline-flex min-h-11 w-full items-center justify-center rounded-lg bg-primary-600 px-4 text-sm font-medium text-white hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600"
        >
          View profile
        </Link>
        {actions && <div className="min-w-0">{actions}</div>}
      </div>
    </article>
  )
}

export default TalentCard
